// =============================================================================
// PREVIOUS IMPLEMENTATION — kept verbatim below for reference, intentionally
// inert. Line comments rather than a /* */ block: the original text contains
// comment terminators, which would close a block comment early.
//
// The active implementation follows after this block. Nothing above it is live
// code — it is here so the previous behaviour stays readable in place.
// =============================================================================

// import React, { ChangeEvent, FC, useState } from "react";
// import StepFooter from "../StepFooter";
// import { PlusIcon } from "../icons";
// import { PanelProps } from "./types";
// import { useLocale } from "../../i18n/locale";
//
// // The tenant may add up to 15 photos of their own (spec, step 5).
// const MAX_TENANT_PHOTOS = 15;
//
// // TODO: the cleaning photos come from the DMS — the pictures taken after the
// // last cleaning. Placeholder until that contract exists.
// const CLEANING_PHOTOS = Array.from({ length: 6 }, (_, i) => ({
//   id: `cleaning-${i + 1}`,
//   src: "/logo/serviceBtn1.png",
// }));
//
// /** Step 5. First of the after-arrival steps. */
// const InventoryPanel: FC<PanelProps> = ({ onBack, onContinue }) => {
//   const { t } = useLocale();
//   const [comments, setComments] = useState("");
//   const [photos, setPhotos] = useState<File[]>([]);
//
//   const remaining = MAX_TENANT_PHOTOS - photos.length;
//
//   const handleAddPhotos = (e: ChangeEvent<HTMLInputElement>) => {
//     const chosen = Array.from(e.target.files || []);
//     if (!chosen.length) return;
//     // Silently keeping only what fits would hide files the tenant picked, so
//     // cap the input instead and let the counter show the limit.
//     setPhotos((prev) => [...prev, ...chosen].slice(0, MAX_TENANT_PHOTOS));
//   };
//
//   const handleConfirm = () => {
//     // TODO: submit the inventory (comments + photos) and capture the tenant's
//     // signature before advancing.
//     onContinue?.();
//   };
//
//   return (
//     <>
//       <section>
//         <h2 className="text-[14px] font-semibold text-navy-900">
//           {t.inventory.cleaningPhotos}
//         </h2>
//         <p className="mt-0.5 text-[12px] text-neutral-500">
//           {t.inventory.cleaningPhotosCaption}
//         </p>
//
//         <ul className="mt-4 grid grid-cols-3 gap-3">
//           {CLEANING_PHOTOS.map((photo) => (
//             <li key={photo.id}>
//               <img
//                 src={photo.src}
//                 alt=""
//                 className="aspect-[3/2] w-full object-cover"
//               />
//             </li>
//           ))}
//         </ul>
//
//         {/* TODO: open the full cleaning-photo gallery. */}
//         <button
//           type="button"
//           className="mt-4 border border-neutral-200 px-5 py-3 text-[12px] font-semibold uppercase tracking-wide text-navy-900 transition-colors hover:border-primary-6000 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-6000"
//         >
//           {t.inventory.viewAllPhotos}
//         </button>
//       </section>
//
//       <section className="mt-8 border-t border-neutral-100 pt-8">
//         <h2 className="text-[14px] font-semibold text-navy-900">
//           {t.inventory.yourCheck}
//         </h2>
//         <p className="mt-0.5 text-[12px] leading-relaxed text-neutral-500">
//           {t.inventory.yourCheckCaption}
//         </p>
//
//         <label htmlFor="workspace-inventory-comments" className="sr-only">
//           {t.inventory.commentsLabel}
//         </label>
//         <textarea
//           id="workspace-inventory-comments"
//           value={comments}
//           onChange={(e) => setComments(e.target.value)}
//           rows={4}
//           placeholder={t.inventory.commentsPlaceholder}
//           className="mt-4 w-full resize-none border border-neutral-200 px-4 py-3 text-[13px] text-neutral-700 placeholder:text-neutral-400 focus:border-primary-6000 focus:outline-none focus:ring-1 focus:ring-primary-6000"
//         />
//
//         <p className="mt-5 text-[12px] font-semibold text-navy-900">
//           {t.inventory.photosUpTo(MAX_TENANT_PHOTOS)}
//         </p>
//
//         {photos.length > 0 && (
//           <ul className="mt-3 grid grid-cols-5 gap-2">
//             {photos.map((photo, i) => (
//               <li key={`${photo.name}-${i}`}>
//                 <img
//                   src={URL.createObjectURL(photo)}
//                   alt={photo.name}
//                   className="aspect-square w-full object-cover"
//                 />
//               </li>
//             ))}
//           </ul>
//         )}
//
//         <label
//           htmlFor="workspace-tenant-photos"
//           className={`mt-3 flex cursor-pointer items-center justify-center gap-2 border border-dashed py-8 text-[12px] font-semibold transition-colors focus-within:ring-2 focus-within:ring-primary-6000 ${
//             remaining > 0
//               ? "border-neutral-300 text-navy-900 hover:border-primary-6000"
//               : "cursor-not-allowed border-neutral-200 text-neutral-300"
//           }`}
//         >
//           <PlusIcon className="h-4 w-4" />
//           {remaining > 0
//             ? t.inventory.addPhotos
//             : t.inventory.limitReached(MAX_TENANT_PHOTOS)}
//           <input
//             id="workspace-tenant-photos"
//             type="file"
//             accept="image/jpeg,image/png"
//             multiple
//             disabled={remaining <= 0}
//             className="sr-only"
//             onChange={handleAddPhotos}
//           />
//         </label>
//       </section>
//
//       <StepFooter
//         onBack={onBack}
//         onContinue={handleConfirm}
//         label={t.desktop.steps.inventory.cta}
//       />
//     </>
//   );
// };
//
// export default InventoryPanel;

// ==== ACTIVE IMPLEMENTATION ==================================================

import React, { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PanelHeading from "../PanelHeading";
import StepFooter from "../StepFooter";
import NoticeBar from "../NoticeBar";
import LegalCheck from "../LegalCheck";
import SignatureLaunchCard from "../SignatureLaunchCard";
import { useReservation } from "../reservationContext";
import { useOnboardingRecord } from "../onboardingContext";
import { PanelProps } from "./types";
import { useLocale } from "../../i18n/locale";
import {
  formatStayDate,
  formatSignatureDate,
  hasCheckInPassed,
} from "../../i18n/formatDate";

/** How many of the inspection photos are shown before the "+N" tile. */
const VISIBLE_PHOTOS = 4;

// TODO: the inspection photos come from the DMS — the set taken after the last
// cleaning. Placeholder tiles until that contract exists.
const GALLERY = Array.from({ length: VISIBLE_PHOTOS }, (_, i) => ({
  id: `inspection-${i + 1}`,
  src: "/logo/serviceBtn1.png",
}));

/**
 * Step 5, the first after check-in. The tenant reviews what the team
 * photographed, optionally adds their own, and signs.
 *
 * Deliberately not an exhaustive condition report — the notice at the top says
 * so, and the confirmation repeats it, because that is the expectation the
 * inventory is meant to set.
 */
const InventoryPanel: FC<PanelProps> = ({
  state,
  update,
  startSigning,
  onBack,
  onContinue,
}) => {
  const { locale, t } = useLocale();
  const reservation = useReservation();
  const c = t.desktop.inventoryPanel;
  const photoInput = useRef<HTMLInputElement>(null);
  const [confirmed, setConfirmed] = useState(true);

  const onboarding = useOnboardingRecord();

  // The photos taken at the property's last cleaning. The placeholder tiles are
  // used only when there is no reservation behind the screen.
  const allPhotos = onboarding ? onboarding.inventory.cleaning_photos : GALLERY.map((g) => g.src);
  const gallery = allPhotos
    .slice(0, VISIBLE_PHOTOS)
    .map((src, i) => ({ id: `inspection-${i + 1}`, src }));

  // Europea's cleaning photos are what the tenant signs against, so their
  // presence — not the separate inspectionPhotoCount field — is what decides
  // whether this step can be completed at all.
  const hasInspectionPhotos = allPhotos.length > 0;

  /**
   * The inventory is a record of the apartment as the tenant found it, so it
   * cannot be signed before they are in it. Measured in the property's own
   * time, not the tenant's device clock.
   */
  const checkInReached = hasCheckInPassed(reservation.checkIn);

  // Both conditions are the back office's: the photos are the last cleaning
  // before arrival, and the arrival itself has to have happened.
  const canSign = hasInspectionPhotos && checkInReached;

  const hidden = reservation.inspectionPhotoCount - VISIBLE_PHOTOS;
  const photoCount = state.tenantPhotos.length;
  // Photos already uploaded with a submitted inventory. Once signed, the picked
  // File bodies are dropped and only their URLs remain, so this is what keeps
  // the tenant's own photos from looking lost when they come back to the step.
  const uploadedPhotoCount = state.inventoryPhotoUrls?.length || 0;

  // Fullscreen viewer for the inspection photos. `null` = closed; otherwise the
  // index into allPhotos being shown.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i + allPhotos.length - 1) % allPhotos.length)),
    [allPhotos.length]
  );
  const showNext = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i + 1) % allPhotos.length)),
    [allPhotos.length]
  );

  // Keyboard control + body-scroll lock while the viewer is open.
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") showPrev();
      else if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  const handleAddPhotos = (e: ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(e.target.files || []);
    // Clearing the input lets the tenant pick the same files again after
    // removing them — without it the browser sees no change and stays silent.
    e.target.value = "";
    if (!chosen.length) return;
    // Trimmed to the cap rather than refused: keeping the first of an oversized
    // selection is friendlier than rejecting the lot. The server enforces the
    // same limit, because this one is only a courtesy.
    update({
      tenantPhotos: [...state.tenantPhotos, ...chosen].slice(
        0,
        reservation.maxTenantPhotos
      ),
    });
  };

  return (
    <>
      <PanelHeading eyebrow={c.eyebrow} title={c.title} subtitle={c.subtitle} />

      <NoticeBar
        className="mb-[18px] mt-5"
        title={c.noticeTitle}
        body={c.noticeBody}
      />

      <div className="mb-2.5 flex items-center justify-between gap-4">
        <div>
          <strong className="block font-serif text-[16px] font-normal text-navy">
            {c.galleryTitle}
          </strong>
          {/* Only once there are photos to have been taken. With none on file
              the caption dated a cleaning that has not been photographed, and
              contradicted the notice below saying the team has not added them
              yet. */}
          {hasInspectionPhotos && (
            <small className="block text-[13px] min-[760px]:text-[13px] text-[#969b9f]">
              {c.galleryCaption(formatStayDate(reservation.checkIn, locale))}
            </small>
          )}
        </div>
        <span className="flex-none bg-[#f3eee6] px-[9px] py-[7px] text-[12px] min-[760px]:text-[12px] text-[#876839]">
          {c.photoCount(reservation.inspectionPhotoCount)}
        </span>
      </div>

      {/* Skipped entirely when empty: an empty grid still contributes its
          bottom margin, leaving an unexplained gap above the notice. */}
      {hasInspectionPhotos && (
      <div className="mb-[15px] grid grid-cols-2 gap-[7px] min-[760px]:grid-cols-4">
        {gallery.map((photo, i) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => setLightboxIndex(i)}
            className="group relative h-[102px] cursor-pointer overflow-hidden border-0 p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
          >
            <img
              src={photo.src}
              alt=""
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.04]"
            />
            {i === VISIBLE_PHOTOS - 1 && hidden > 0 && (
              <span className="absolute inset-0 grid place-items-center bg-[#12263fa8] text-[13px] font-bold text-white">
                {c.morePhotos(hidden)}
              </span>
            )}
          </button>
        ))}
      </div>
      )}

      <div className="flex flex-wrap items-center gap-[14px] border border-line px-[14px] py-[13px]">
        <div className="flex-1">
          <strong className="block text-[14px] font-semibold text-navy">
            {c.yourPhotos}
          </strong>
          <small className="block text-[13px] min-[760px]:text-[13px] text-[#959b9f]">
            {c.yourPhotosCaption(reservation.maxTenantPhotos)}
          </small>
        </div>

        <button
          type="button"
          onClick={() => photoInput.current?.click()}
          className="flex-none border border-[#d6cbbb] bg-white px-[11px] py-[9px] text-[12px] min-[760px]:text-[12px] font-bold text-[#946e3b] transition-colors hover:border-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
        >
          ＋ {c.addPhotos}
        </button>
        {/*
          sr-only, not the `hidden` attribute: index.css styles `[multiple]` as a
          block, full-width field in the base layer, which beats the user agent's
          [hidden] rule and would render the native "Choose Files" control.
        */}
        <input
          ref={photoInput}
          type="file"
          accept="image/*"
          multiple
          className="sr-only"
          onChange={handleAddPhotos}
        />
      </div>

      {photoCount > 0 && (
        <div className="flex items-center gap-2 bg-[#f4f8f4] px-3 py-[9px] text-[11px] min-[760px]:text-[10px] text-[#55705b]">
          <span className="font-extrabold" aria-hidden="true">
            ✓
          </span>
          {c.photosReady(photoCount)}
          <button
            type="button"
            onClick={() => update({ tenantPhotos: [] })}
            className="ml-auto text-[11px] min-[760px]:text-[9px] text-[#85725c] underline focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
          >
            {c.remove}
          </button>
        </div>
      )}

      {/* No freshly-picked photos, but the tenant already submitted some with a
          signed inventory — show that they are on file rather than nothing. */}
      {photoCount === 0 && uploadedPhotoCount > 0 && (
        <div className="flex items-center gap-2 bg-[#f4f8f4] px-3 py-[9px] text-[11px] min-[760px]:text-[10px] text-[#55705b]">
          <span className="font-extrabold" aria-hidden="true">
            ✓
          </span>
          {c.photosSubmitted(uploadedPhotoCount)}
        </div>
      )}

      {/* The "Your comments" textarea was removed on request: the tenant records
          the apartment's condition with photos only, and does not write anything.
          `inventoryComments` is left in the state and still submitted (as an
          empty string) so the signature payload keeps its shape — see
          confirmSignature in TenantDashboardPage. Note this reverses an earlier
          explicit requirement that the tenant be able to add comments as well as
          photos, so the field may be asked for again. */}

      <LegalCheck
        id="inventory-confirm"
        checked={confirmed}
        onChange={setConfirmed}
        className="mb-3.5 mt-3.5"
      >
        {c.confirm(reservation.inspectionPhotoCount)}
      </LegalCheck>

      {/* Signing an inventory with no inspection photos on it would produce a
          signed record of nothing — the tenant would be attesting to a
          condition report that does not exist. Until Europea's cleaning photos
          are on the record there is nothing to review, so the hand-off is
          closed and the wait is explained rather than left as a dead button. */}
      {!checkInReached ? (
        // Named first when both are outstanding: the cleaning photos are taken
        // right before arrival, so a stay that has not started explains the
        // missing photos too, and two stacked notices only read as noise.
        <NoticeBar
          className="mb-3.5"
          body={c.awaitingCheckIn(formatStayDate(reservation.checkIn, locale))}
        />
      ) : (
        !hasInspectionPhotos && (
          <NoticeBar className="mb-3.5" body={c.awaitingPhotos} />
        )
      )}

      <SignatureLaunchCard
        eyebrow={t.desktop.contractPanel.signatureEyebrow}
        title={t.desktop.contractPanel.signatureTitle}
        signedTitle={c.signatureDone}
        caption={t.desktop.contractPanel.signatureCaption}
        signedCaption={t.desktop.contractPanel.signedBy(
          reservation.tenantName,
          formatSignatureDate(locale)
        )}
        signed={state.inventorySigned}
        signature={state.inventorySignature}
        actionLabel={c.signCta}
        disabled={!canSign}
        // Same rule as the contract step: correctable while the tenant is still
        // here, sealed once Continue has moved them past it.
        committed={onboarding?.steps.inventory?.state === "completed"}
        onSign={() => {
          if (!confirmed || !canSign) return;
          startSigning("inventory");
        }}
      />

      <StepFooter
        onBack={onBack}
        backLabel={c.back}
        onContinue={onContinue}
        label={c.cta}
        // Same gate as the contract step: the inventory is a signed record of
        // the property's condition at check-in, so moving on without it would
        // leave the stay with no agreed baseline to settle against later.
        disabled={!state.inventorySigned}
      />

      {lightboxIndex !== null && allPhotos[lightboxIndex] &&
        // Rendered into <body> so the fixed backdrop always covers the whole
        // viewport — inside the workspace grid a transformed ancestor would
        // otherwise scope `fixed` to the panel and leave the backdrop patchy.
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#050505f2] p-4"
            role="dialog"
            aria-modal="true"
            onClick={closeLightbox}
          >
            <button
              type="button"
              aria-label={c.galleryClose}
              onClick={closeLightbox}
              className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-[#ffffff1a] text-2xl leading-none text-white ring-1 ring-[#ffffff59] transition-colors hover:bg-[#ffffff33] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              ✕
            </button>

            {allPhotos.length > 1 && (
              <button
                type="button"
                aria-label={c.galleryPrev}
                onClick={(e) => {
                  e.stopPropagation();
                  showPrev();
                }}
                className="absolute left-4 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-[#ffffff1a] text-3xl leading-none text-white ring-1 ring-[#ffffff59] transition-colors hover:bg-[#ffffff33] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                ‹
              </button>
            )}

            <img
              src={allPhotos[lightboxIndex]}
              alt=""
              className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />

            {allPhotos.length > 1 && (
              <button
                type="button"
                aria-label={c.galleryNext}
                onClick={(e) => {
                  e.stopPropagation();
                  showNext();
                }}
                className="absolute right-4 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-[#ffffff1a] text-3xl leading-none text-white ring-1 ring-[#ffffff59] transition-colors hover:bg-[#ffffff33] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                ›
              </button>
            )}

            {allPhotos.length > 1 && (
              <span className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-[#ffffff1a] px-3 py-1 text-[12px] font-semibold text-white ring-1 ring-[#ffffff33]">
                {lightboxIndex + 1} / {allPhotos.length}
              </span>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default InventoryPanel;
